import { useCallback } from 'react'
import DashboardField from "DashboardUser/components/DashboardField"
import Filters from "DashboardUser/components/Filters"
import { useAct } from 'DashboardUser/store'
import { isMobile } from 'react-device-detect';
import RecordModal from 'DashboardUser/components/RecordModal';
import DashboardColumnHeader from './DashboardColumnHeader';

export default function DashboardTargetTable({ preview = false }) {
  const act = useAct();
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
    setShowScrollIndicator(el.scrollHeight > el.clientHeight && !isAtBottom);
  };

  // Getting data from your state management
  const dashboardTargets = useSel(s => _.orderBy(s.dashboardTargets.ids.map(id => s.dashboardTargets.entities[id]), '_position'));
  const { currentPage, totalPages, expandedDashboardTargetId } = useSel(s => s.local);
  const dashboardFields = useSel(s => _.orderBy(s.dashboard.dashboardFields, 'digitOrder', 'asc'));
  const dataLoading = useSel(s => s.local.dataLoading);

  const targetCount = dashboardTargets.length;

  useEffect(() => {
    handleScroll();
    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [targetCount, currentPage]);

  // Pagination
  const handlePageChange = (page) => {
    page = Math.max(0, Math.min(page, totalPages));
    act.local.upsert({ currentPage: page });
  }

  return (
    <>
      <div className="team-table-container box">
        {dataLoading && <div className="table-loading-bar" />}
        {!preview && <>
          <div>
            <Filters />
          </div>
        </>}
        <div className="table-scroll-wrapper">
          <div className={`table-scroll-area custom-scrollbar ${dataLoading ? 'table-loading' : ''}`} ref={scrollRef} onScroll={handleScroll}>
            <table className="team-table">
              <thead>
                <tr>
                  <th className='meta-col'></th>
                  {dashboardFields.map(df =>
                    <DashboardColumnHeader key={df.id} field={df} />
                  )}
                </tr>
              </thead>
              <tbody>
                {dashboardTargets.map((dashboardTarget, index) => (
                  <DashboardTargetRow
                    key={dashboardTarget.id || index}
                    dashboardTargetId={dashboardTarget.id}
                  />
                ))}
              </tbody>
            </table>
            {showScrollIndicator && (
              <div className="scroll-down-indicator">
                <span>Scroll for more ↓</span>
              </div>
            )}
          </div>
        </div>
        {dashboardTargets.length === 0 && <div className='no-data'>No members found with current filters.</div>}
        {dashboardTargets.length > 0 && !preview && <div className="team-table-footer">
          <div className="pagination">
            <button className='button pagination-prev' onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <SvgIconsBackCaret />
            </button>
            <div className="pagination-pages">
              <input type="text" className='pagination-current' value={currentPage} onChange={e => handlePageChange(Number(e.target.value))} />
              <span className='pagination-text'>of</span>
              <span className='pagination-total'>{totalPages}</span>
            </div>
            <button className='button pagination-next' onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
              <SvgIconsForwardCaret />
            </button>
          </div>
        </div>}
      </div>
      {expandedDashboardTargetId && <RecordModal />}
    </>
  );
}

const DashboardTargetRow = ({ dashboardTargetId }) => {
  const act = useAct();
  const expanded = useSel(s => s.local.expandedDashboardTargetId === dashboardTargetId);
  const dashboardTarget = useSel(s => s.dashboardTargets.entities[dashboardTargetId]);
  const dashboardFields = useSel(s => _.orderBy(s.dashboard.dashboardFields, 'digitOrder'));

  const [fieldBackgroundColors, setFieldBackgroundColors] = useState({});

  const toggleExpanded = () => {
    if (expanded) {
      act.local.upsert({ expandedDashboardTargetId: null });
    } else {
      act.local.upsert({ expandedDashboardTargetId: dashboardTargetId });
    }
  }

  const handleBackgroundColorChange = useCallback((fieldId, backgroundColor) => {
    setFieldBackgroundColors(prev => {
      if (prev[fieldId] === backgroundColor) return prev;
      return {
        ...prev,
        [fieldId]: backgroundColor
      };
    });
  }, []);

  const interactiveSources = ['answerable_question', 'completable_goal', 'resubscription'];

  return <tr className={`${expanded ? 'expanded' : ''}`} onClick={() => isMobile ? toggleExpanded() : null}>
    <td className="expand-row-icon meta-col" onClick={toggleExpanded}>
      {expanded ? <SvgIconsClose /> : <SvgIconsEdit color='currentColor' />}
    </td>
    {dashboardFields.map(df =>
      <td
        key={df.id}
        className={fieldBackgroundColors[df.id] || ''}
        onClick={interactiveSources.includes(df.source) ? (e) => e.stopPropagation() : undefined}
      >
        <DashboardField
          field={df}
          dashboardTarget={dashboardTarget}
          onBackgroundColorChange={handleBackgroundColorChange}
        />
      </td>
    )}
  </tr>
};
